import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDocumentVersion, useRestoreVersion } from '@/hooks/useKnowledge';
import { VersionContent } from '@/components/knowledge/VersionContent';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, RotateCcw } from 'lucide-react';

export function KnowledgeVersionViewPage() {
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(`/knowledge/${id}/versions`);
    }
  };

  const { data: version, isLoading } = useDocumentVersion(id!, versionId!);
  const restoreMutation = useRestoreVersion(id!);

  const handleRestore = async () => {
    await restoreMutation.mutateAsync(versionId!);
    setShowRestoreDialog(false);
    navigate(`/knowledge/${id}`);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="text-center py-12 text-muted-foreground">
          Carregando versão...
        </div>
      </div>
    );
  }

  if (!version) {
    return (
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="text-center py-12 text-muted-foreground">
          Versão não encontrada
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" onClick={handleBack} className="shrink-0">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold break-words">Visualizar Versão {version.version}</h1>
        </div>
        <Button
          onClick={() => setShowRestoreDialog(true)}
          variant="outline"
          className="w-full sm:w-auto sm:ml-auto"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Restaurar esta versão
        </Button>
      </div>

      <VersionContent version={version} />

      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar restauração</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a restaurar a versão {version.version}. Isso criará uma nova versão
              do documento com o conteúdo desta versão. O histórico completo será preservado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? 'Restaurando...' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
